import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "./childrensBookAssetState";
import {
  normalizeManuscriptContent,
  plainTextFromManuscriptEntry,
} from "./childrensBookManuscriptContent";

const MAX_COMPLETE_FRONT_PANEL_CROP_FRACTION =
  0.05;

const MINIMUM_SPINE_TEXT_PAGE_COUNT =
  80;

const TRIM_PROFILES = {
  "8_x_8_in": {
    width: 8,
    height: 8,
    unit: "in",
  },

  "8.25_x_8.25_in": {
    width: 8.25,
    height: 8.25,
    unit: "in",
  },

  "8.5_x_8.5_in": {
    width: 8.5,
    height: 8.5,
    unit: "in",
  },

  "8_x_10_in": {
    width: 8,
    height: 10,
    unit: "in",
  },

  "8.5_x_11_in": {
    width: 8.5,
    height: 11,
    unit: "in",
  },

  "5.5_x_8.5_in": {
    width: 5.5,
    height: 8.5,
    unit: "in",
  },

  "6_x_9_in": {
    width: 6,
    height: 9,
    unit: "in",
  },

  a4: {
    width: 210,
    height: 297,
    unit: "mm",
  },

  a5: {
    width: 148,
    height: 210,
    unit: "mm",
  },
};

function clean(value) {
  return String(value ?? "").trim();
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function normalizedOrientation(value) {
  const candidate = clean(value);

  return [
    "portrait",
    "landscape",
    "square",
  ].includes(candidate)
    ? candidate
    : "";
}

function normalizedTrimProfile(
  visualFormat = {}
) {
  const trimSize =
    clean(visualFormat.trimSize);

  const customTrimSize =
    clean(
      visualFormat.customTrimSize
    );

  const orientation =
    normalizedOrientation(
      visualFormat.orientation
    );

  const base =
    TRIM_PROFILES[trimSize];

  if (!base) {
    return {
      key: trimSize,
      label:
        trimSize ===
        "custom_dimensions"
          ? customTrimSize
          : trimSize,
      width: null,
      height: null,
      unit: "",
      orientation,
      measurable: false,
    };
  }

  let width = base.width;
  let height = base.height;

  if (
    orientation === "landscape" &&
    height > width
  ) {
    [width, height] = [
      height,
      width,
    ];
  }

  if (
    orientation === "portrait" &&
    width > height
  ) {
    [width, height] = [
      height,
      width,
    ];
  }

  return {
    key: trimSize,
    label: trimSize,
    width,
    height,
    unit: base.unit,
    orientation,
    measurable: true,
  };
}

function issue(
  code,
  severity,
  scope,
  targetId = ""
) {
  return {
    code,
    severity,
    scope,
    targetId,
  };
}


function completeFrontPanelAspectCompatibility({
  frontCover = null,
  trim = {},
} = {}) {
  const dimensions =
    frontCover &&
    typeof frontCover ===
      "object"
      ? (
          frontCover
            .verifiedImageDimensions ||
          {}
        )
      : {};

  const sourceWidth =
    Number(
      dimensions.widthPixels
    );

  const sourceHeight =
    Number(
      dimensions.heightPixels
    );

  const targetWidth =
    Number(
      trim?.width
    );

  const targetHeight =
    Number(
      trim?.height
    );

  if (
    !frontCover ||
    trim?.measurable !== true ||
    !Number.isFinite(
      sourceWidth
    ) ||
    sourceWidth <= 0 ||
    !Number.isFinite(
      sourceHeight
    ) ||
    sourceHeight <= 0 ||
    !Number.isFinite(
      targetWidth
    ) ||
    targetWidth <= 0 ||
    !Number.isFinite(
      targetHeight
    ) ||
    targetHeight <= 0
  ) {
    return {
      evaluated: false,
      compatible: null,
      maximumCropFraction:
        MAX_COMPLETE_FRONT_PANEL_CROP_FRACTION,
    };
  }

  const sourceAspectRatio =
    sourceWidth /
    sourceHeight;

  const targetAspectRatio =
    targetWidth /
    targetHeight;

  const retainedFraction =
    Math.min(
      sourceAspectRatio /
        targetAspectRatio,

      targetAspectRatio /
        sourceAspectRatio
    );

  const cropFraction =
    Math.max(
      0,
      1 - retainedFraction
    );

  return {
    evaluated: true,

    compatible:
      cropFraction <=
      MAX_COMPLETE_FRONT_PANEL_CROP_FRACTION,

    sourceAspectRatio,

    targetAspectRatio,

    estimatedCropFraction:
      cropFraction,

    maximumCropFraction:
      MAX_COMPLETE_FRONT_PANEL_CROP_FRACTION,
  };
}

export function buildChildrensBookPublicationProofSpec(
  projectPlan = {},
  childrensBookAssets = []
) {
  const identity =
    projectPlan.identity || {};

  const visualFormat =
    projectPlan.visualFormat || {};

  const coverPlan =
    projectPlan.coverPlan || {};

  const units = safeArray(
    projectPlan.bookMap?.units
  );

  const manuscriptEntries =
    safeArray(
      projectPlan
        .manuscriptPlan
        ?.entries
    );

  const manuscriptsByUnit =
    manuscriptEntries.reduce(
      (result, entry) => {
        const unitId =
          clean(entry?.unitId);

        if (unitId) {
          result[unitId] = entry;
        }

        return result;
      },
      {}
    );

  const illustrationsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const frontCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const backCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "back_cover"
    );

  const authorPhoto =
    currentChildrensBookCover(
      childrensBookAssets,
      "author_photo"
    );

  const title =
    clean(identity.title) ||
    clean(coverPlan.title);

  const author =
    clean(identity.penName) ||
    clean(identity.authorName) ||
    clean(coverPlan.authorName);

  const trim =
    normalizedTrimProfile(
      visualFormat
    );

  const frontCoverCompatibility =
    completeFrontPanelAspectCompatibility({
      frontCover,
      trim,
    });

  const publicationFormat =
    clean(
      visualFormat.publicationFormat
    );

  const coverType =
    clean(coverPlan.coverType) ||
    "front_only";

  const coverMode =
    coverType === "full_print_wrap"
      ? "wraparound"
      : coverType === "digital_cover"
        ? "digital"
        : "front_only";

  let nextPageNumber = 1;

  const interiorItems =
    units.map(
      (unit, index) => {
        const unitId =
          clean(unit?.id) ||
          `unit-${index + 1}`;

        const manuscript =
          manuscriptsByUnit[
            unitId
          ] || {};

        const manuscriptText =
          clean(
            plainTextFromManuscriptEntry(
              manuscript
            )
          );

        const manuscriptContentResult =
          normalizeManuscriptContent(
            manuscript
              ?.manuscriptContent
          );

        const manuscriptContent =
          manuscriptContentResult.ok &&
          manuscriptContentResult
            .content?.blocks.length
            ? manuscriptContentResult
                .content
            : null;

        const manuscriptStatus =
          clean(
            manuscript?.status
          );

        const manuscriptComplete =
          manuscriptStatus ===
          "complete";

        const illustration =
          illustrationsByUnit[
            unitId
          ] || null;

        const requiresIllustration =
          Boolean(
            clean(
              unit?.illustrationPlan
            ) &&
            unit.illustrationPlan !==
              "none"
          );

        const textRequired =
          clean(
            unit?.textAmount
          ).toLowerCase() !==
          "none";

        const pageSpan =
          unit?.unitType ===
          "double_page_spread"
            ? 2
            : 1;

        /*
         * Use the same structural page sequence already
         * adopted by the Children's Book writer workflow.
         *
         * A left-to-right two-page spread begins on an
         * even verso page. If the next page is odd, that
         * page is reserved for spread alignment.
         */
        if (
          pageSpan === 2 &&
          nextPageNumber % 2 === 1
        ) {
          nextPageNumber += 1;
        }

        const pageStart =
          nextPageNumber;

        const pageEnd =
          pageStart +
          pageSpan -
          1;

        nextPageNumber =
          pageEnd + 1;

        const pageLabel =
          pageEnd > pageStart
            ? `Pages ${pageStart}–${pageEnd}`
            : `Page ${pageStart}`;

        /*
         * Book Structure owns whether manuscript text
         * belongs on this page or spread.
         *
         * Historical manuscript data may remain stored
         * after the writer chooses No text. Keep that
         * source history, but do not present it as current
         * Publication Proof content.
         */
        const proofManuscriptText =
          textRequired
            ? manuscriptText
            : "";

        const proofManuscriptContent =
          textRequired
            ? manuscriptContent
            : null;

        return {
          id: `proof-${unitId}`,
          sourceUnitId: unitId,
          order: index,
          unitType:
            clean(unit?.unitType) ||
            "single_page",
          pageSpan,
          pageStart,
          pageEnd,
          pageLabel,
          workingTitle:
            clean(
              unit?.workingTitle
            ),
          manuscriptText:
            proofManuscriptText,
          manuscriptContent:
            proofManuscriptContent,
          manuscriptStatus,
          manuscriptComplete,
          textRequired,
          illustrationRequirement:
            clean(
              unit
                ?.illustrationPlan
            ),
          illustrationPageSide:
            clean(
              unit
                ?.illustrationPageSide
            ).toLowerCase(),
          requiresIllustration,
          illustration,
          textReady:
            !textRequired ||
            Boolean(
              manuscriptText
            ),
          illustrationReady:
            !requiresIllustration ||
            Boolean(illustration),
          ready:
            (
              !textRequired ||
              (
                Boolean(
                  manuscriptText
                ) &&
                manuscriptComplete
              )
            ) &&
            (
              !requiresIllustration ||
              Boolean(illustration)
            ),
        };
      }
    );

  const estimatedPageCount =
    interiorItems.reduce(
      (
        total,
        item
      ) =>
        Math.max(
          total,
          Number(
            item?.pageEnd
          ) || 0
        ),
      0
    );

  const spineTextEligibleByPageCount =
    coverMode === "wraparound" &&
    estimatedPageCount >=
      MINIMUM_SPINE_TEXT_PAGE_COUNT;

  const issues = [];

  if (!title) {
    issues.push(
      issue(
        "missing_title",
        "blocking",
        "identity"
      )
    );
  }

  if (!author) {
    issues.push(
      issue(
        "missing_author",
        "blocking",
        "identity"
      )
    );
  }

  if (!trim.label) {
    issues.push(
      issue(
        "missing_trim_size",
        "blocking",
        "format"
      )
    );
  }

  if (
    !publicationFormat ||
    publicationFormat ===
      "not_decided"
  ) {
    issues.push(
      issue(
        "missing_publication_format",
        "blocking",
        "format"
      )
    );
  }

  if (units.length === 0) {
    issues.push(
      issue(
        "missing_interior_units",
        "blocking",
        "interior"
      )
    );
  }

  if (!frontCover) {
    issues.push(
      issue(
        "missing_front_cover",
        "blocking",
        "cover"
      )
    );
  }

  if (
    frontCover &&
    frontCoverCompatibility
      .evaluated === true &&
    frontCoverCompatibility
      .compatible === false
  ) {
    issues.push(
      issue(
        "front_cover_aspect_ratio_incompatible",
        "blocking",
        "cover"
      )
    );
  }

  if (
    coverMode === "wraparound" &&
    !clean(
      coverPlan.backCoverBlurb
    )
  ) {
    issues.push(
      issue(
        "missing_back_cover_text",
        "blocking",
        "cover"
      )
    );
  }

  if (
    coverMode === "wraparound"
  ) {
    issues.push(
      issue(
        "spine_width_requires_print_profile",
        "warning",
        "cover"
      )
    );
  }

  for (
    const item of interiorItems
  ) {
    if (
      item.textRequired &&
      !item.textReady
    ) {
      issues.push(
        issue(
          "missing_manuscript_text",
          "blocking",
          "interior",
          item.sourceUnitId
        )
      );
    } else if (
      item.textRequired &&
      !item.manuscriptComplete
    ) {
      issues.push(
        issue(
          "manuscript_not_complete",
          "blocking",
          "interior",
          item.sourceUnitId
        )
      );
    }

    if (
      !item.illustrationReady
    ) {
      issues.push(
        issue(
          "missing_required_illustration",
          "blocking",
          "interior",
          item.sourceUnitId
        )
      );
    }
  }

  const blockingIssues =
    issues.filter(
      (item) =>
        item.severity ===
        "blocking"
    );

  const warningIssues =
    issues.filter(
      (item) =>
        item.severity ===
        "warning"
    );

  return {
    version: 1,
    category: "children_book",
    source: "derived_blueprint",

    identity: {
      title,
      subtitle:
        clean(identity.subtitle),
      author,
      language:
        clean(identity.language),
    },

    format: {
      publicationFormat,
      trim,
      bleed:
        clean(
          visualFormat.bleed
        ),
      textPlacement:
        clean(
          visualFormat.textPlacement
        ),
    },

    cover: {
      mode: coverMode,
      coverType,
      frontAsset: frontCover,

      frontPanel: {
        role:
          frontCover
            ? "complete_front_panel"
            : "planned_front_panel",

        aspectCompatibility:
          frontCoverCompatibility,

        textOverlayAllowed:
          !frontCover,

        titleOverlayAllowed:
          !frontCover,

        authorOverlayAllowed:
          !frontCover,
      },

      backAsset:
        coverMode === "wraparound"
          ? backCover
          : null,

      authorPhotoAsset:
        coverMode === "wraparound"
          ? authorPhoto
          : null,
      backImageRole:
        [
          "author_photo",
          "illustration",
          "other",
        ].includes(
          clean(
            coverPlan.backCoverImageRole
          ).toLowerCase()
        )
          ? clean(
              coverPlan.backCoverImageRole
            ).toLowerCase()
          : "",

      backImageMode:
        [
          "none",
          "artwork",
          "background",
        ].includes(
          clean(
            coverPlan.backCoverImageMode
          ).toLowerCase()
        )
          ? clean(
              coverPlan.backCoverImageMode
            ).toLowerCase()
          : backCover
            ? "artwork"
            : "none",

      backTitleMode:
        [
          "auto",
          "show",
          "hide",
        ].includes(
          clean(
            coverPlan.backCoverTitleMode
          ).toLowerCase()
        )
          ? clean(
              coverPlan.backCoverTitleMode
            ).toLowerCase()
          : "auto",

      backTitleFontFamily:
        [
          "Georgia",
          "Times New Roman",
          "Arial",
          "Verdana",
          "Courier New",
        ].includes(
          clean(
            coverPlan
              .backCoverTitleFontFamily
          )
        )
          ? clean(
              coverPlan
                .backCoverTitleFontFamily
            )
          : "",

      backTitleFontSize:
        Number.isFinite(
          Number(
            coverPlan
              .backCoverTitleFontSize
          )
        ) &&
        Number(
          coverPlan
            .backCoverTitleFontSize
        ) >= 10 &&
        Number(
          coverPlan
            .backCoverTitleFontSize
        ) <= 36
          ? Number(
              coverPlan
                .backCoverTitleFontSize
            )
          : null,

      backTitleColour:
        /^#[0-9a-f]{6}$/i.test(
          clean(
            coverPlan
              .backCoverTitleColour
          )
        )
          ? clean(
              coverPlan
                .backCoverTitleColour
            ).toLowerCase()
          : "",

      backTitleAlignment:
        [
          "auto",
          "left",
          "center",
          "right",
        ].includes(
          clean(
            coverPlan
              .backCoverTitleAlignment
          ).toLowerCase()
        )
          ? clean(
              coverPlan
                .backCoverTitleAlignment
            ).toLowerCase()
          : "auto",

      backTitleLineSpacing:
        Number.isFinite(
          Number(
            coverPlan
              .backCoverTitleLineSpacing
          )
        ) &&
        Number(
          coverPlan
            .backCoverTitleLineSpacing
        ) >= 1 &&
        Number(
          coverPlan
            .backCoverTitleLineSpacing
        ) <= 2
          ? Number(
              coverPlan
                .backCoverTitleLineSpacing
            )
          : null,

      backBlurbFontFamily:
        [
          "Georgia",
          "Times New Roman",
          "Arial",
          "Verdana",
          "Courier New",
        ].includes(
          clean(
            coverPlan
              .backCoverBlurbFontFamily
          )
        )
          ? clean(
              coverPlan
                .backCoverBlurbFontFamily
            )
          : "",

      backBlurbFontSize:
        Number.isFinite(
          Number(
            coverPlan
              .backCoverBlurbFontSize
          )
        ) &&
        Number(
          coverPlan
            .backCoverBlurbFontSize
        ) >= 8 &&
        Number(
          coverPlan
            .backCoverBlurbFontSize
        ) <= 24
          ? Number(
              coverPlan
                .backCoverBlurbFontSize
            )
          : null,

      backBlurbColour:
        /^#[0-9a-f]{6}$/i.test(
          clean(
            coverPlan
              .backCoverBlurbColour
          )
        )
          ? clean(
              coverPlan
                .backCoverBlurbColour
            ).toLowerCase()
          : "",

      backBlurbAlignment:
        [
          "auto",
          "left",
          "center",
          "right",
          "justify",
        ].includes(
          clean(
            coverPlan
              .backCoverBlurbAlignment
          ).toLowerCase()
        )
          ? clean(
              coverPlan
                .backCoverBlurbAlignment
            ).toLowerCase()
          : "auto",

      backBlurbLineSpacing:
        Number.isFinite(
          Number(
            coverPlan
              .backCoverBlurbLineSpacing
          )
        ) &&
        Number(
          coverPlan
            .backCoverBlurbLineSpacing
        ) >= 1 &&
        Number(
          coverPlan
            .backCoverBlurbLineSpacing
        ) <= 2
          ? Number(
              coverPlan
                .backCoverBlurbLineSpacing
            )
          : null,
      title:
        clean(coverPlan.title) ||
        title,
      subtitle:
        clean(
          coverPlan.subtitle
        ),
      author:
        clean(
          coverPlan.authorName
        ) ||
        author,
      illustratorCredit:
        clean(
          coverPlan
            .illustratorCredit
        ),
      contributorCredits:
        clean(
          coverPlan
            .contributorCredits
        ),
      backCoverText:
        clean(
          coverPlan
            .backCoverBlurb
        ),
      spineTitleMode:
        [
          "auto",
          "show",
          "hide",
        ].includes(
          clean(
            coverPlan.spineTitleMode
          ).toLowerCase()
        )
          ? clean(
              coverPlan.spineTitleMode
            ).toLowerCase()
          : "auto",
      spineAuthorMode:
        [
          "auto",
          "show",
          "hide",
        ].includes(
          clean(
            coverPlan.spineAuthorMode
          ).toLowerCase()
        )
          ? clean(
              coverPlan.spineAuthorMode
            ).toLowerCase()
          : "auto",
      spineText:
        clean(
          coverPlan.spineText
        ),
      rightsNote:
        clean(
          coverPlan.rightsNote
        ),
      spine: {
        status:
          coverMode !==
          "wraparound"
            ? "not_applicable"
            : spineTextEligibleByPageCount
              ? "requires_print_profile"
              : "ineligible_page_count",

        eligibleForText:
          spineTextEligibleByPageCount,

        minimumPageCountForText:
          MINIMUM_SPINE_TEXT_PAGE_COUNT,

        pageCountBasis:
          estimatedPageCount,

        width: null,
        unit: "",
      },
    },

    interior: {
      unitCount:
        interiorItems.length,
      estimatedPageCount,
      items: interiorItems,
    },

    readiness: {
      viewerReady:
        interiorItems.length > 0,
      contentReady:
        blockingIssues.length === 0,
      blockingIssueCount:
        blockingIssues.length,
      warningIssueCount:
        warningIssues.length,
    },

    issues,
  };
}
